<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from './components/ContactNoteItem.vue';

const props = defineProps({
  loadError: { type: String, default: '' },
  loading: Boolean,
});
const { t } = useI18n();
const store = useStore();
const route = useRoute();

const message = defineModel({ type: String, default: '' });
const saveError = ref('');
const pending = ref(false);
const deleting = ref(null);
const context = `${route.params.accountId}-${route.params.contactId}`;
const isCurrent = () =>
  context === `${route.params.accountId}-${route.params.contactId}`;

const currentUser = useMapGetter('getCurrentUser');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const notes = computed(() => notesByContact.value(route.params.contactId));

const getWrittenBy = note => {
  const isCurrentUser = note?.user?.id === currentUser.value.id;
  return isCurrentUser
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : note?.user?.name || 'Bot';
};

const onAdd = async content => {
  if (!content?.trim() || pending.value || isCreatingNote.value) return;
  pending.value = true;
  saveError.value = '';
  try {
    await store.dispatch('contactNotes/create', {
      content,
      contactId: route.params.contactId,
    });
    if (isCurrent() && message.value === content) message.value = '';
  } catch {
    if (isCurrent())
      saveError.value = t('WORKSPACE_V2.CONTACT_DETAIL.SAVE_ERROR');
  } finally {
    pending.value = false;
  }
};
const onDelete = async noteId => {
  if (!noteId || deleting.value || uiFlags.value.isDeleting) return;
  deleting.value = noteId;
  saveError.value = '';
  try {
    await store.dispatch('contactNotes/delete', {
      noteId,
      contactId: route.params.contactId,
    });
  } catch {
    if (isCurrent())
      saveError.value = t('WORKSPACE_V2.CONTACT_DETAIL.DELETE_ERROR');
  } finally {
    deleting.value = null;
  }
};

const keyboardEvents = {
  '$mod+Enter': {
    action: () => onAdd(message.value),
    allowOnFocusedInput: true,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="flex flex-col gap-6">
    <p
      v-if="saveError"
      role="alert"
      class="mx-5 rounded-xl border border-n-weak p-3 text-sm text-n-ruby-11"
    >
      {{ saveError }}
    </p>
    <Editor
      v-model="message"
      :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
      focus-on-mount
      class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4 px-6"
    >
      <template #actions>
        <div class="flex items-center gap-3">
          <Button
            variant="link"
            color="blue"
            size="sm"
            :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
            class="hover:no-underline"
            :is-loading="isCreatingNote || pending"
            :disabled="!message.trim() || isCreatingNote || pending"
            @click="onAdd(message)"
          />
        </div>
      </template>
    </Editor>
    <div
      v-if="isFetchingNotes || props.loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="!loadError && notes.length > 0">
      <ContactNoteItem
        v-for="note in notes"
        :key="note.id"
        class="mx-6 py-4"
        :note="note"
        :written-by="getWrittenBy(note)"
        :allow-delete="!deleting"
        @delete="onDelete"
      />
    </div>
    <p
      v-else-if="!loadError"
      class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EMPTY_STATE') }}
    </p>
  </div>
</template>
